import { ArrowLeft, ArrowRight } from 'lucide-react';

import { Button } from '@/shared/components/shadcn/button';
import { cn } from '@/shared/utils/cn';
import type { ReactTable, RowData } from '@tanstack/react-table';
import type { CreatorTableFeatures } from '../data-table/features';

export interface NavigationProps<TData extends RowData> {
  table: ReactTable<CreatorTableFeatures, TData>;
  className?: string;
}

export function PageNavigation<T extends RowData>({ table, className }: NavigationProps<T>) {
  const getStartIndex = () =>
    table.state.pagination.pageIndex * table.state.pagination.pageSize + 1;

  const getEndIndex = () =>
    Math.min(
      (table.state.pagination.pageIndex + 1) * table.state.pagination.pageSize,
      table.getCoreRowModel().rows.length,
    );

  return (
    <PageNavigationView
      startIndex={getStartIndex()}
      endIndex={getEndIndex()}
      className={className}
      onPrevious={table.previousPage}
      onNext={table.nextPage}
      totalCount={table.getCoreRowModel().rows.length}
      canGoNext={table.getCanNextPage()}
      canGoPrevious={table.getCanPreviousPage()}
    />
  );
}

export function PageNavigationView({
  className,
  startIndex,
  endIndex,
  totalCount,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
}: {
  className?: string;
  onPrevious: () => void;
  onNext: () => void;
  startIndex: number;
  endIndex: number;
  totalCount: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-2 px-6 py-4', className)}>
      <Button disabled={!canGoPrevious} variant="outline" onClick={onPrevious}>
        <ArrowLeft size={16} />
        <span>Previous</span>
      </Button>
      <div>
        <span className="text-base-foreground text-sm font-medium">
          {startIndex}-{endIndex}
        </span>{' '}
        <span className="text-base-muted-foreground text-sm font-normal">of {totalCount}</span>
      </div>
      <Button disabled={!canGoNext} variant="outline" onClick={onNext}>
        <span>Next</span>
        <ArrowRight size={16} />
      </Button>
    </div>
  );
}
